/**
 * useCampaignPreview: who a send would reach, and each person's outcome, for
 * a course and an audience (plan docs/plans/lms-plan.md 8.2 step 2 "the
 * wizard shows the resolved list with a count", 18.16; WP-43; reused by any
 * screen that sends through POST /admin/campaigns).
 *
 *   const { preview, loading, error, reload } =
 *     useCampaignPreview({ courseId, audience, courseVersionId?, force? });
 *
 * Asks POST /admin/campaigns/preview (api/campaigns.ts previewCampaign; it
 * writes nothing) whenever the course, the audience, the pinned version or
 * force change, and holds the latest answer; an answer for inputs that have
 * since changed is dropped. Nothing is asked (preview null) until a course is
 * chosen and the audience names someone. The untick is NOT sent here: the
 * caller keeps the excluded ids and filters the list itself, so an unticked
 * person stays on screen to be ticked again (AudiencePreview).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorMessage } from '../../api/client';
import { audienceIsEmpty, previewCampaign } from '../../api/campaigns';
import type { CampaignAudience, CampaignPreview } from '../../api/campaigns';

export interface CampaignPreviewInput {
  courseId: string | null;
  audience: CampaignAudience;
  courseVersionId?: string | null;
  force?: boolean;
}

export interface CampaignPreviewState {
  preview: CampaignPreview | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useCampaignPreview({ courseId, audience, courseVersionId = null, force = false }: CampaignPreviewInput): CampaignPreviewState {
  const [preview, setPreview] = useState<CampaignPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const request = useRef(0);

  // One stable key for the inputs, so a re-render with equal values asks nothing.
  const key = JSON.stringify({ courseId, audience, courseVersionId, force });

  useEffect(() => {
    const id = ++request.current;
    const input = JSON.parse(key) as { courseId: string | null; audience: CampaignAudience; courseVersionId: string | null; force: boolean };
    if (!input.courseId || audienceIsEmpty(input.audience)) {
      setPreview(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    previewCampaign({
      course_id: input.courseId,
      audience: input.audience,
      course_version_id: input.courseVersionId,
      force: input.force,
    })
      .then((answer) => {
        if (id !== request.current) return;
        setPreview({
          recipients: Array.isArray(answer?.recipients) ? answer.recipients : [],
          count: typeof answer?.count === 'number' ? answer.count : 0,
        });
      })
      .catch((err) => {
        if (id !== request.current) return;
        setPreview(null);
        setError(apiErrorMessage(err, 'Could not work out who this would reach.'));
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [key, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return { preview, loading, error, reload };
}

export default useCampaignPreview;
